import { CacheProvider } from '@emotion/react'
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import theme from '../theme/devicesTheme'
import { globalStyles } from '../theme/globalStyles'
import { createEmotionCache } from 'app-core'

type DevicesProvidersProps = {
  children: ReactNode
}

// Client singleton — avoids duplicate <style> tags on re-renders.
// Server: fresh cache per request (never module-level on SSR).
const clientSideEmotionCache =
  typeof window !== 'undefined' ? createEmotionCache() : undefined

/**
 * App-wide MUI + Emotion providers (same pattern as ePharmacy Providers).
 */
export function DevicesProviders({ children }: DevicesProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
  )

  const emotionCache = useMemo(() => {
    if (typeof window !== 'undefined' && clientSideEmotionCache) {
      return clientSideEmotionCache
    }
    return createEmotionCache()
  }, [])

  return (
    <CacheProvider value={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles styles={globalStyles} />
          {children}
        </ThemeProvider>
      </QueryClientProvider>
    </CacheProvider>
  )
}
